/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import React, { FC, SVGAttributes, useState } from "react";
import { useSelector } from "react-redux";
import { EventInfo } from "../../utility";
import { Store } from "../../store";
import { useConcurrentPlay } from "hooks/Streaming/ConcurrentPlay";
import { useI18n } from "hooks/i18n/i18n";
import { Box } from "@material-ui/core";
import { EventDescription } from "features/events/EventDescription";

interface StreamingEventDetailProps {
  event: EventInfo;
  currentVid: string; // current watching video id. if null, user has not purchased any video
  // changeDrawerState?: (state: 'chat'|'gift') => void
}
/**
 * イベント概要エリア
 * @param props
 */
export const StreamingEventDetailV2: React.FC<StreamingEventDetailProps> = (
  props
) => {
  const { t } = useI18n();

  const [isCollapseOpen, setIsCollapseOpen] = useState<boolean>(false);

  const isAdmin = useSelector((state: Store) => state.admin.isAdmin);
  const isDarkModeEnabled = useSelector(
    (state: Store) => state.streaming.streamingSettings.isDarkModeEnabled
  );
  const eventVideoMap = useSelector(
    (state: Store) => state.streaming.eventVideoMap
  );
  const streamingType = useSelector(
    (state: Store) => state.streaming.streamingType
  );

  const isConcurrentPlayEnabled =
    props.event.streamingInfo?.optionSettings?.isConcurrentPlayEnabled;
  const concurrentPlay = useConcurrentPlay(
    props.currentVid,
    streamingType,
    isConcurrentPlayEnabled
  );

  if (props.event === null || !props.currentVid || eventVideoMap === null) {
    return <Loading />;
  }

  const currentVideo = eventVideoMap[props.currentVid];
  if (!currentVideo) {
    return <Loading />;
  }

  const videoTitle = currentVideo.name
    ? `${props.event.eventTitle}【${currentVideo.name}】`
    : props.event.eventTitle;

  const handleToggleCollapse = () => {
    setIsCollapseOpen((prev) => !prev);
  };
  const outlineDetailButtonColorStyle = css`
    color: ${isDarkModeEnabled ? "#fff" : "#000"};
  `;
  const outlineDetailButtonCss = [
    outlineDetailStyleBase,
    outlineDetailButtonColorStyle,
  ];
  const outlineDetailButtonLabel = isCollapseOpen
    ? t("streaming.detail.hideOutline")
    : t("streaming.detail.showOutline");
  const outlineDetailButtonIcon = isCollapseOpen ? (
    <ArrowUpIcon
      style={{
        width: "12px",
        height: "auto",
      }}
    />
  ) : (
    <ArrowDownIcon
      style={{
        width: "12px",
        height: "auto",
      }}
    />
  );

  return (
    <Box display={"flex"} flexDirection={"column"} gridGap={"12px"}>
      <Box
        display={"flex"}
        flexDirection={"column"}
        gridGap={"16px"}
        borderBottom={"1px solid #e0e0e0"}
      >
        <Box
          component={"h1"}
          color={isDarkModeEnabled ? "dark.text.primary" : "light.text.primary"}
          style={{ fontSize: "20px", lineHeight: "1.5em" }}
        >
          {videoTitle}
        </Box>

        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            gridGap: "12px",
            paddingBottom: "16px",
          }}
        >
          <Box
            component={"p"}
            color={
              isDarkModeEnabled ? "dark.text.primary" : "light.text.primary"
            }
            style={{ fontSize: "14px", fontWeight: "bold" }}
          >
            {props.event.artists}
          </Box>

          {/* streamingTypeがliveの時、かつisOncurrentPlayEnabledがtrueの時表示する */}
          {((streamingType === "Live" && isConcurrentPlayEnabled) ||
            isAdmin) && (
            <Box
              component={"p"}
              color={
                isDarkModeEnabled ? "dark.text.primary" : "light.text.primary"
              }
              style={{ fontSize: "14px" }}
            >
              {concurrentPlay.toLocaleString()}{" "}
              {t("streaming.detail.countPlay")}
            </Box>
          )}
        </Box>
      </Box>

      <button css={outlineDetailButtonCss} onClick={handleToggleCollapse}>
        {outlineDetailButtonLabel}
        {outlineDetailButtonIcon}
      </button>
      <EventDescription
        description={props.event.description}
        appearance={{
          isDarkModeEnabled,
          isCompact: true,
          isTruncatedToOneLine: !isCollapseOpen,
        }}
      />
    </Box>
  );
};

const Loading: React.FC = () => {
  return (
    <div className="info">
      <p className="title"></p>
      <p className="artist"></p>
      <p className="description"></p>
    </div>
  );
};

const outlineDetailStyleBase = css`
  align-items: center;
  background-color: transparent;
  border: none;
  cursor: pointer;
  display: inline-flex;
  font-size: 14px;
  gap: 4px;
  justify-content: right;
  list-style: none;
  margin-left: auto;
`;

const ArrowDownIcon: FC<SVGAttributes<SVGElement>> = (props) => {
  return (
    <svg viewBox="0 0 10 7" {...props}>
      <path d="M8.825 0L5 3.81667L1.175 0L0 1.175L5 6.175L10 1.175L8.825 0Z" />
    </svg>
  );
};

const ArrowUpIcon: FC<SVGAttributes<SVGElement>> = (props) => {
  return (
    <svg viewBox="0 0 10 7" {...props}>
      <path d="M5 0L0 5L1.175 6.175L5 2.35833L8.825 6.175L10 5L5 0Z" />
    </svg>
  );
};
